/**
 * GIS Service
 * Handles all interactions with ArcGIS REST API
 */

import { featureServices, queryConfig } from '../config/gisConfig';

/**
 * Query features by attribute using GeoJSON backend
 * Now uses /api/geojson/:layer endpoints for TPL real data
 */
export async function queryByAttribute(layerKey, whereClause, options = {}) {
  const service = featureServices[layerKey];

  if (!service) {
    throw new Error(`Invalid layer: ${layerKey}. Please check your configuration.`);
  }

  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

    // Build query parameters for GeoJSON endpoint
    const params = new URLSearchParams();
    if (whereClause && whereClause !== '1=1') {
      params.set('where', whereClause);
    }
    if (options.maxResults) {
      params.set('limit', options.maxResults.toString());
    }

    // Use GeoJSON endpoint for TPL real data
    const url = `${backendUrl}/api/geojson/${layerKey}${params.toString() ? '?' + params.toString() : ''}`;
    console.log('Querying GeoJSON endpoint:', url);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error || 'Query failed');
    }

    return {
      features: data.features || [],
      count: data.totalCount || data.features?.length || 0,
      layer: layerKey,
      query: whereClause
    };
  } catch (error) {
    console.error('Query failed:', error);
    throw new Error(`Query failed: ${error.message}`);
  }
}

/**
 * Calculate extent (bounding box) from GeoJSON features
 * Useful for zooming to results
 */
export async function queryExtent(layerKey, whereClause) {
  try {
    // Query features first
    const result = await queryByAttribute(layerKey, whereClause, { maxResults: 500 });

    if (!result.features || result.features.length === 0) {
      return null;
    }

    // Calculate extent from all features
    let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;

    for (const feature of result.features) {
      if (!feature.geometry) continue;

      const coords = feature.geometry.coordinates;
      const type = feature.geometry.type;

      // Helper to update bounds from coordinate array
      const updateBounds = (coordArray) => {
        if (Array.isArray(coordArray[0])) {
          coordArray.forEach(updateBounds);
        } else {
          const [lon, lat] = coordArray;
          if (lon < xmin) xmin = lon;
          if (lon > xmax) xmax = lon;
          if (lat < ymin) ymin = lat;
          if (lat > ymax) ymax = lat;
        }
      };

      if (type === 'Point') {
        updateBounds(coords);
      } else if (type === 'LineString' || type === 'Polygon' || type === 'MultiLineString' || type === 'MultiPolygon') {
        updateBounds(coords);
      }
    }

    if (xmin === Infinity) return null;

    return { xmin, ymin, xmax, ymax };
  } catch (error) {
    console.error('Extent calculation failed:', error);
    return null;
  }
}

/**
 * Get feature count without returning geometries
 */
export async function queryCount(layerKey, whereClause) {
  const service = featureServices[layerKey];

  if (!service || !service.url) {
    throw new Error(`Invalid layer: ${layerKey}`);
  }

  try {
    const params = new URLSearchParams({
      where: whereClause || '1=1',
      returnCountOnly: 'true',
      f: 'json'
    });

    const url = `${service.url}/query?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('Count query failed:', error);
    return 0;
  }
}

/**
 * Query by object IDs
 */
export async function queryByIds(layerKey, objectIds) {
  const service = featureServices[layerKey];

  if (!service || !service.url) {
    throw new Error(`Invalid layer: ${layerKey}`);
  }

  try {
    const params = new URLSearchParams({
      objectIds: objectIds.join(','),
      outFields: queryConfig.outFields.join(','),
      returnGeometry: 'true',
      f: 'json'
    });

    const url = `${service.url}/query?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'Query failed');
    }

    return {
      features: data.features || [],
      count: data.features?.length || 0,
      layer: layerKey
    };
  } catch (error) {
    console.error('ID query failed:', error);
    throw new Error(`Query by IDs failed: ${error.message}`);
  }
}

/**
 * Validate if a service URL is accessible
 */
export async function validateService(url) {
  try {
    const response = await fetch(`${url}?f=json`);
    const data = await response.json();
    return {
      valid: !data.error,
      name: data.name,
      fields: data.fields?.map(f => f.name) || []
    };
  } catch (error) {
    return {
      valid: false,
      error: error.message
    };
  }
}

/**
 * Build SQL WHERE clause from conditions
 */
export function buildWhereClause(conditions) {
  if (!conditions || conditions.length === 0) {
    return '1=1';
  }

  const clauses = conditions.map(cond => {
    const { field, operator, value, type } = cond;

    // Handle IN operator with array values
    if (operator === 'IN') {
      const values = Array.isArray(value) ? value : [value];
      if (type === 'string') {
        const quotedValues = values.map(v => `'${String(v).replace(/'/g, "''")}'`).join(',');
        return `${field} IN (${quotedValues})`;
      } else if (type === 'number') {
        return `${field} IN (${values.join(',')})`;
      }
    }

    // Handle BETWEEN operator
    if (operator === 'BETWEEN') {
      const values = Array.isArray(value) ? value : [value, value];
      if (type === 'string') {
        return `${field} BETWEEN '${values[0]}' AND '${values[1]}'`;
      } else if (type === 'date') {
        return `${field} BETWEEN DATE '${values[0]}' AND DATE '${values[1]}'`;
      } else if (type === 'number') {
        return `${field} BETWEEN ${values[0]} AND ${values[1]}`;
      }
    }

    // Handle array values (shouldn't happen, but just in case)
    if (Array.isArray(value)) {
      console.warn(`Array value with operator ${operator}, converting to IN`);
      const quotedValues = value.map(v => `'${String(v).replace(/'/g, "''")}'`).join(',');
      return `${field} IN (${quotedValues})`;
    }

    // Handle single values
    if (type === 'string' || typeof value === 'string') {
      // String values need single quotes
      return `${field} ${operator} '${String(value).replace(/'/g, "''")}'`;
    } else if (type === 'date') {
      // Date handling
      return `${field} ${operator} DATE '${value}'`;
    } else if (type === 'number') {
      // Numeric values don't need quotes
      return `${field} ${operator} ${value}`;
    } else {
      // Default: treat as string
      return `${field} ${operator} '${value}'`;
    }
  });

  return clauses.join(' AND ');
}

/**
 * Execute a complete GIS query from natural language intent
 */
export async function executeGISQuery(intent) {
  const { layer, conditions, zoom = true, sortBy, proximity_to, within_ft, premise_type, groupBy } = intent;

  // Build WHERE clause
  const whereClause = buildWhereClause(conditions);

  console.log('Executing GIS query:', {
    layer,
    whereClause,
    zoom,
    proximity_to,
    within_ft
  });

  // Query features
  let results = await queryByAttribute(layer, whereClause);

  // Emissions proximity: e.g. "Grade 2 leaks within 100ft of school/hospital"
  // Fetch the target premise layer, compute Haversine distance, filter and tag each kept feature.
  if (proximity_to && results.features?.length) {
    const proxResults = await queryByAttribute(proximity_to, '1=1');
    let premises = proxResults.features || [];
    if (premise_type && premise_type !== 'both') {
      premises = premises.filter((p) => {
        const t = (p.properties?.address_type || '').toLowerCase();
        return t.includes(premise_type);
      });
    }
    const FT_PER_DEG_LAT = 364320; // ~ at mid-latitudes
    const within = within_ft || 100;
    const keptFeatures = [];
    results.features.forEach((leak) => {
      const lc = leak.geometry?.coordinates;
      if (!lc) return;
      const [llon, llat] = lc;
      let bestDist = Infinity;
      let bestPremise = null;
      premises.forEach((prem) => {
        const pc = prem.geometry?.coordinates;
        if (!pc) return;
        const [plon, plat] = pc;
        // Approximate planar distance in feet (fine for short distances < 1 mile)
        const dx = (plon - llon) * FT_PER_DEG_LAT * Math.cos((llat * Math.PI) / 180);
        const dy = (plat - llat) * FT_PER_DEG_LAT;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < bestDist) {
          bestDist = d;
          bestPremise = prem;
        }
      });
      if (bestDist <= within && bestPremise) {
        keptFeatures.push({
          ...leak,
          properties: {
            ...leak.properties,
            nearest_premise: bestPremise.properties?.name || bestPremise.properties?.id || 'Premise',
            nearest_premise_type: bestPremise.properties?.address_type || 'sensitive',
            nearest_distance_ft: Math.round(bestDist)
          }
        });
      }
    });
    // Sort by distance ascending so closest premises come first
    keptFeatures.sort((a, b) => a.properties.nearest_distance_ft - b.properties.nearest_distance_ft);
    const proximityPremises = new Set();
    keptFeatures.forEach(f => proximityPremises.add(f.properties.nearest_premise));
    const matchedPremises = premises
      .filter((p) => proximityPremises.has(p.properties?.name) || proximityPremises.has(p.properties?.id))
      .map((p) => ({ ...p, _sourceLayer: 'sensitive_premises' }));
    const taggedLeaks = keptFeatures.map(f => ({ ...f, _sourceLayer: 'leaks' }));
    results = {
      features: [...taggedLeaks, ...matchedPremises],
      count: keptFeatures.length,
      // expose layers so map renders both
      layers: ['leaks', 'sensitive_premises']
    };
    console.log(`Proximity filter: ${keptFeatures.length} leaks within ${within}ft of ${matchedPremises.length} sensitive premises`);
  }
  console.log('Query results:', {
    count: results.count,
    features: results.features?.length,
    hasGeometry: results.features?.[0]?.geometry ? true : false
  });

  // Get extent if zoom requested
  let extent = null;
  if (zoom && results.count > 0) {
    extent = await queryExtent(layer, whereClause);
    console.log('Extent received:', extent);
  }

  const finalResults = {
    ...results,
    extent,
    zoom,
    // Surface intent metadata so the Explain panel can render the reasoning trail.
    layer: results.layers?.length > 1 ? 'multi' : layer,
    layers: results.layers,
    intent: intent.intent || layer,
    conditions: conditions || [],
    sortBy,
    proximity_to,
    within_ft,
    groupBy
  };
  console.log('Returning final results to map:', {
    count: finalResults.count,
    zoom: finalResults.zoom,
    hasExtent: !!finalResults.extent
  });

  return finalResults;
}

/**
 * Calculate distance between two lat/lon points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @param {string} unit - Unit of measurement ('miles' or 'kilometers')
 * @returns {number} Distance in specified unit
 */
export function calculateDistance(lat1, lon1, lat2, lon2, unit = 'miles') {
  // Earth's radius in miles and kilometers
  const R = unit === 'kilometers' ? 6371 : 3959;

  // Convert degrees to radians
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
function toRadians(degrees) {
  return degrees * Math.PI / 180;
}

/**
 * Calculate the centroid (center point) of a polygon
 * @param {Array} rings - Array of coordinate rings [[lon, lat], ...]
 * @returns {Object} {lat, lon} of centroid
 */
export function calculateCentroid(rings) {
  if (!rings || !rings[0] || rings[0].length === 0) {
    throw new Error('Invalid polygon rings');
  }

  const coordinates = rings[0]; // Use first ring (outer boundary)

  let sumLat = 0;
  let sumLon = 0;
  let count = 0;

  // Calculate average of all coordinates (excluding closing point if duplicate)
  for (let i = 0; i < coordinates.length - 1; i++) {
    sumLon += coordinates[i][0];
    sumLat += coordinates[i][1];
    count++;
  }

  return {
    lat: sumLat / count,
    lon: sumLon / count
  };
}

/**
 * Find tracts within a specified radius of a center tract
 * @param {string} centerTractId - TractID of center point
 * @param {Array} allFeatures - All tract features from GeoJSON
 * @param {number} radius - Search radius
 * @param {string} unit - Unit of measurement ('miles' or 'kilometers')
 * @returns {Object} {centerTract, nearbyTracts[]}
 */
export function findNearbyTracts(centerTractId, allFeatures, radius, unit = 'miles') {
  // Find the center tract using TractID
  const centerFeature = allFeatures.find(feature => {
    const props = feature.attributes || feature.properties || {};
    return props.TractID === centerTractId || props.WellName === centerTractId || props.API === centerTractId;
  });

  if (!centerFeature) {
    throw new Error(`Center feature ${centerTractId} not found`);
  }

  // Calculate center feature centroid (handle both GeoJSON and ArcGIS formats)
  const centerGeometry = centerFeature.geometry;
  let centerCentroid;

  if (centerGeometry.rings) {
    // ArcGIS format
    centerCentroid = calculateCentroid(centerGeometry.rings);
  } else if (centerGeometry.coordinates) {
    // GeoJSON format
    centerCentroid = calculateGeoJSONCentroid(centerGeometry);
  } else {
    throw new Error('Unknown geometry format');
  }

  // Find all features within radius
  const nearbyTracts = [];

  for (const feature of allFeatures) {
    const props = feature.attributes || feature.properties || {};
    const featureId = props.TractID || props.WellName || props.API;

    // Skip the center feature itself
    if (featureId === centerTractId) {
      continue;
    }

    // Calculate this feature's centroid
    let featureCentroid;
    try {
      if (feature.geometry.rings) {
        featureCentroid = calculateCentroid(feature.geometry.rings);
      } else if (feature.geometry.coordinates) {
        featureCentroid = calculateGeoJSONCentroid(feature.geometry);
      } else {
        continue;
      }
    } catch (e) {
      continue;
    }

    // Calculate distance from center
    const distance = calculateDistance(
      centerCentroid.lat,
      centerCentroid.lon,
      featureCentroid.lat,
      featureCentroid.lon,
      unit
    );

    // If within radius, add to results
    if (distance <= radius) {
      nearbyTracts.push({
        feature,
        distance: parseFloat(distance.toFixed(2)),
        centroid: featureCentroid
      });
    }
  }

  // Sort by distance (closest first)
  nearbyTracts.sort((a, b) => a.distance - b.distance);

  return {
    centerTract: {
      feature: centerFeature,
      centroid: centerCentroid
    },
    nearbyTracts,
    totalFound: nearbyTracts.length
  };
}

/**
 * Calculate centroid from GeoJSON geometry
 */
function calculateGeoJSONCentroid(geometry) {
  const coords = geometry.coordinates;
  const type = geometry.type;

  let allCoords = [];

  // Flatten coordinates based on geometry type
  if (type === 'Point') {
    return { lon: coords[0], lat: coords[1] };
  } else if (type === 'LineString') {
    allCoords = coords;
  } else if (type === 'Polygon') {
    allCoords = coords[0]; // Use outer ring
  } else if (type === 'MultiLineString') {
    coords.forEach(line => allCoords.push(...line));
  } else if (type === 'MultiPolygon') {
    coords.forEach(poly => allCoords.push(...poly[0]));
  }

  if (allCoords.length === 0) {
    throw new Error('No coordinates found');
  }

  let sumLon = 0, sumLat = 0;
  for (const coord of allCoords) {
    sumLon += coord[0];
    sumLat += coord[1];
  }

  return {
    lon: sumLon / allCoords.length,
    lat: sumLat / allCoords.length
  };
}

/**
 * Find tracts within a specified radius of MULTIPLE center tracts (OR logic)
 * @param {Array<string>} centerIds - Array of TractID/WellName/API values to use as centers
 * @param {Array} allFeatures - All tract features from GeoJSON
 * @param {number} radius - Search radius
 * @param {string} unit - Unit of measurement ('miles' or 'kilometers')
 * @returns {Object} {centerTracts[], nearbyTracts[], totalFound}
 */
export function findNearbyTractsMultiCenter(centerIds, allFeatures, radius, unit = 'miles') {
  const results = {
    centerTracts: [],
    nearbyTracts: [],
    totalFound: 0
  };

  const seenFeatureIds = new Set();

  // For each center, find nearby features
  for (const centerId of centerIds) {
    try {
      const proximityResult = findNearbyTracts(centerId, allFeatures, radius, unit);

      // Add center tract
      results.centerTracts.push(proximityResult.centerTract);

      // Add nearby tracts (avoiding duplicates)
      for (const nearbyTract of proximityResult.nearbyTracts) {
        const props = nearbyTract.feature.attributes || nearbyTract.feature.properties || {};
        const featureId = props.TractID || props.WellName || props.API || 'unknown';

        if (!seenFeatureIds.has(featureId)) {
          // First time seeing this feature
          results.nearbyTracts.push({
            ...nearbyTract,
            nearCenters: [centerId], // Track which centers this is near
            minDistance: nearbyTract.distance // Track minimum distance
          });
          seenFeatureIds.add(featureId);
        } else {
          // Already seen - add this center to the list and update min distance
          const existing = results.nearbyTracts.find(nt => {
            const ntProps = nt.feature.attributes || nt.feature.properties || {};
            const ntId = ntProps.TractID || ntProps.WellName || ntProps.API;
            return ntId === featureId;
          });
          if (existing) {
            existing.nearCenters.push(centerId);
            existing.minDistance = Math.min(existing.minDistance, nearbyTract.distance);
          }
        }
      }
    } catch (error) {
      console.error(`Failed to find nearby features for center ${centerId}:`, error.message);
      // Continue with other centers
    }
  }

  // Sort by minimum distance (closest first)
  results.nearbyTracts.sort((a, b) => a.minDistance - b.minDistance);

  results.totalFound = results.nearbyTracts.length;
  return results;
}

/**
 * Convert miles to meters (for Leaflet circle radius)
 * @param {number} miles - Distance in miles
 * @returns {number} Distance in meters
 */
export function milesToMeters(miles) {
  return miles * 1609.34;
}

/**
 * Convert kilometers to meters (for Leaflet circle radius)
 * @param {number} kilometers - Distance in kilometers
 * @returns {number} Distance in meters
 */
export function kilometersToMeters(kilometers) {
  return kilometers * 1000;
}

export default {
  queryByAttribute,
  queryExtent,
  queryCount,
  queryByIds,
  validateService,
  buildWhereClause,
  executeGISQuery,
  calculateDistance,
  calculateCentroid,
  findNearbyTracts,
  findNearbyTractsMultiCenter,
  milesToMeters,
  kilometersToMeters
};
